import type { ReactNode } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

import { generateFileName } from '../utils/file';

import { makeAPIRequestReactQueryWithHeaders } from './api';
import {
  type ClaimSummaryGenerateRequest,
  buildClaimSummaryGenerateRequestBody,
} from './claimSummaryGenerate';
import { endpoints } from './endpoints';

interface DownloadPiecesRequest extends ClaimSummaryGenerateRequest {
  /** Patient name for the downloaded filename */
  patientName?: string;
}

interface DownloadPiecesResponse {
  success: boolean;
  message: ReactNode;
}

/**
 * React Query mutation hook for downloading claim attachment pieces as a ZIP file.
 *
 * Calls POST /claim-summary/generate-pieces and triggers download of the resulting ZIP blob.
 *
 * @param options - Optional mutation options
 * @returns React Query mutation object
 */
export const useDownloadPieces = (
  options?: Parameters<
    typeof useMutation<
      DownloadPiecesResponse,
      AxiosError,
      DownloadPiecesRequest
    >
  >[0],
) => {
  const { t } = useLingui();
  const user = useSelector(
    (state: {
      login?: {
        user?: { primaryPracticeId?: string; primaryOrganizationId?: string };
      };
    }) => state?.login?.user,
  );
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  return useMutation({
    mutationFn: async (
      request: DownloadPiecesRequest,
    ): Promise<DownloadPiecesResponse> => {
      if (!currentOrganizationId || !currentPracticeId) {
        throw new Error('Missing organization or practice information');
      }

      const requestBody = buildClaimSummaryGenerateRequestBody(request);

      const response = await makeAPIRequestReactQueryWithHeaders<Blob>(
        endpoints.generatePieces,
        undefined,
        {
          Practice: currentPracticeId,
          Org: currentOrganizationId,
        },
        'POST',
        {
          data: requestBody,
          responseType: 'blob',
        },
      );

      // Create a download link for the ZIP blob
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;

      // Generate file name based on patient name if available, otherwise fall back to unified appointment ID
      link.download = generateFileName(
        'claim-attachment',
        request.unifiedAppointmentId,
        'zip',
        request.patientName,
      );

      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);

      return { success: true, message: t`ZIP downloaded successfully` };
    },
    ...options,
  });
};
